<template lang="pug">
  section.photo-album.container.content(v-if="contents && contents.length" :class="classes" :style="style")
    .column.preview-img-list.is-offset-1.is-10(ref="container" :style="{ height: `${layoutGeometry.containerHeight}px` }")
      .box(
        v-for="(box, index) in boxes"
        :style="{ top: `${box.style.top}px`, left: `${box.style.left}px` }"
      )
        a(href="#" @click.prevent="handleClick($event, index)")
          Picture.preview-img-item(
            :show-spinner="false"
            :image="box.data"
            :lazy="true"
            optimize="speed"
            missing-icon="exclamation-triangle"
            object-fit="contain"
            :title="box.data.title"

            :debug="box.data.id === '1086074'"

            :style="{ width: `${box.style.width}px`, height: `${box.style.height}px` }"
          )
            caption(v-if="box.data.title || striptags(box.data.text)")
              h4 {{ box.data.title }}
              div( v-html="box.data.text" )
</template>

<script>
import { get, cloneDeep, trim, debounce } from '@/helpers';

import striptags from 'striptags';
import justifiedLayout from 'justified-layout';

import router from '@/router';
import { mapGetters } from 'vuex';

import section from '@/mixins/section';
import findGeneratedFile from '@/mixins/findGeneratedFile';

export default {
  name: 'PhotoAlbum',
  mixins: [section, findGeneratedFile],
  data() {
    return {
      config: {
        containerWidth: undefined,
        containerPadding: {
          top: undefined,
          right: undefined,
          bottom: undefined,
          left: undefined
        },
        boxSpacing: 8,
        targetRowHeight: 320
      }
    };
  },
  props: {
    section: {
      type: Object,
      required: true
    }
  },
  computed: {
    contents() {
      let contents = this.section.contents || [];
      return cloneDeep(contents).sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
    },
    images() {
      let images = [];

      this.contents.forEach(content => {
        const image = get(content, 'images.0');
        if (!image) return false;

        image.title = content.title;
        image.text = content.text;

        images.push(image);
      });

      return images;
    },
    ratios() {
      let ratios = [];

      this.images.forEach(image => {
        const fz = get(image, 'pivot.z') || 1;
        const w = get(image, 'pivot.w') || image.width / fz;
        const h = get(image, 'pivot.h') || image.height / fz;

        const ratio = w / h;
        ratios.push(ratio);
      });

      return ratios;
    },
    layoutGeometry() {
      return justifiedLayout(this.ratios, this.config);
    },
    boxes() {
      let boxes = [];

      const calculateFit = (data, maxHeight = null) => {
        const pivot = data.pivot;
        let fit = get(pivot, 'fit') || 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1';

        let mWidth;
        let mHeight = 30;
        let mfit = fit;
        if (window.devicePixelRatio) mHeight *= window.devicePixelRatio;

        // let { w, h, x, y, z } = pivot
        let { w, h, z } = pivot;
        if (!z) z = 1;

        let width = data.width / z;
        let height = data.height / z;
        let aspectRatio = width / height;

        if (maxHeight) {
          if (window.devicePixelRatio) maxHeight *= window.devicePixelRatio;
          maxHeight = Math.min(maxHeight, height);
        }

        if (w && h) {
          aspectRatio = w / h;

          if (maxHeight && h > maxHeight) {
            height = maxHeight;
          }

          if (height * aspectRatio > data.width / z) {
            height = width / aspectRatio;
          } else {
            width = height * aspectRatio;
          }

          fit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${width}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${height}`);

          // calculate preview
          mWidth = mHeight * aspectRatio;
          mfit = fit.replace(/w=([0-9]+\.?[0-9]*)/, `w=${mWidth}`).replace(/h=([0-9]+\.?[0-9]*)/, `h=${mHeight}`);
        } else if (!w && !h) {
          if (maxHeight && height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }

          fit += `&w=${width}`;

          // calculate preview
          mfit += `&h=${mHeight}`;
        }

        if (this.webpSupport) fit = `${fit}&format=webp`;

        return {
          id: data.id,
          // src: `${storageRoute}/images/${data.id}/${data.name}?${fit}`,
          // msrc: `${storageRoute}/images/${data.id}/${data.name}?fit&h=50`,
          // error: `${storageRoute}/images/${data.id}/${data.name}?${fit}&preview=greyscale`,
          // loading: `${storageRoute}/images/${data.id}/${data.name}?${fit}&preview=blur`,

          src: this.findGeneratedFile(data, fit, 'speed'),
          msrc: this.findGeneratedFile(data, mfit, 'speed'),
          loading: this.findGeneratedFile(data, `${fit}&preview=loading`, 'speed'),

          width,
          height,
          aspectRatio
        };
      };

      this.images.forEach((el, i) => {
        // const fullHeight = window.innerHeight;
        let thumbnailHeight = 500;

        const height = this.settings.find(setting => setting.key === 'height');
        if (height && height.value) thumbnailHeight = height.value * 1.2;

        const style = this.layoutGeometry.boxes[i];

        let data, thumbnail, image;
        data = el;

        // image = calculateFit(data, fullHeight);
        image = calculateFit(data);
        thumbnail = calculateFit(data, thumbnailHeight);

        boxes.push({ style, data, thumbnail, image });
      });

      return boxes;
    },

    photoSwipeItems() {
      let items = [];

      this.boxes.forEach(box => {
        const id = box.image.id;
        const src = box.image.src;
        const msrc = box.image.msrc;
        const w = box.image.width;
        const h = box.image.height;

        let title = '';
        if (box.data.title) title += `<h4>${box.data.title}</h4>`;
        if (this.striptags(box.data.text)) title += box.data.text;

        items.push({ id, src, msrc, w, h, title });
      });

      return items;
    },
    ...mapGetters({
      webpSupport: 'webpSupport'
    })
  },
  methods: {
    handleClick(event, i) {
      const content = this.contents[i];
      if (content && content.hyperlink) {
        const link = content.hyperlink.href;

        let href, target;
        switch (content.hyperlink.type) {
          case 'internal': {
            const path = get(content.hyperlink, 'menuItem.path');
            if (path) {
              router.push({ path });
              return;
            }
            break;
          }
          case 'external': {
            if (!link.startsWith('https://') && !link.startsWith('http://')) href = `https://${link}`;
            else href = link;
            target = content.hyperlink.target || '_self';
            break;
          }

          case 'mailto': {
            href = `mailto:${link}`;
            target = '_self';
            break;
          }

          case 'tel': {
            href = `tel:${link}`;
            target = '_self';
            break;
          }
        }

        if (href) window.open(href, target);
        else this.$photoswipe.open(event, i, this.photoSwipeItems);
      } else {
        this.$photoswipe.open(event, i, this.photoSwipeItems);
      }
    },
    striptags(value) {
      return trim(striptags(value));
    },
    calculateDimansions() {
      const spacing = this.settings.find(setting => setting.key === 'spacing');
      if (spacing && spacing.value) this.config.boxSpacing = spacing.value;

      const height = this.settings.find(setting => setting.key === 'height');
      if (height && height.value) this.config.targetRowHeight = height.value;

      if (this.$refs.container) {
        const style = getComputedStyle(this.$refs.container);
        this.config.containerWidth = this.$refs.container.offsetWidth;
        // this.config.containerWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)

        this.config.containerPadding = {
          top: parseInt(style.paddingTop),
          right: parseInt(style.paddingRight),
          bottom: parseInt(style.paddingBottom),
          left: parseInt(style.paddingLeft)
        };
      }
    },
    debouncedCalculateDimensions: debounce(function() {
      this.calculateDimansions();
    })
  },
  mounted() {
    window.addEventListener('resize', this.debouncedCalculateDimensions);
    this.calculateDimansions();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedCalculateDimensions);
  }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.photo-album.container.content .column
  position: relative
  .box
    position: absolute
    & > a
      & > .preview-img-item::v-deep
        transition: transform .3s cubic-bezier(0, 0.9, 0.31, 1.04)
        & > figure img
          transition: box-shadow .3s cubic-bezier(0, 0.9, 0.31, 1.04)
        & > caption
          transition: background .3s cubic-bezier(0, 0.9, 0.31, 1.04), backdrop-filter .3s cubic-bezier(0, 0.9, 0.31, 1.04)
      &:hover > .preview-img-item::v-deep
        position: relative
        z-index: 2
        transform: scale(1.02)
        & > figure img
          // box-shadow: $box-shadow-3
          box-shadow: 0 10px 50px -10px rgba(0, 0, 0, 0.2), 0 40px 80px 0 rgba(0, 0, 0, 0.05)
        & > caption
          // opacity: 0.6
          background: rgba(0,0,0,.3)
          backdrop-filter: blur(2px)
    .picture-container::v-deep
      &.loading
        img
          box-shadow: none
      &.loading picture,
      &.error picture
        width: 100% !important
        height: 100% !important
      figure
        height: 100%
      img
        background: $color-secondary
        display: block
        width: 100%
        height: 100%
      caption
        font-size: .8rem
        position: absolute
        left: 0
        bottom: 0
        padding: 10px
        right: 0
        text-align: left
        color: white
        background: rgba(0,0,0,.8)
        backdrop-filter: blur(6px)
        h4
          color: inherit
          margin-bottom: 0
</style>
